'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import type { Lang } from '../hooks/useLanguage';
import { LANGS, LANG_LABELS } from '../hooks/useLanguage';
import { HeaderAuthButton } from '../components/Auth/HeaderAuthButton';
import { trackToolStart } from '../lib/analytics';
import './HomeLanding.css';

// Apple-style home page: the DAW itself is the hero. Tool landing pages keep
// the sidebar layout in HomePage.tsx; this component only renders `/`.

type Tool = { id: string; icon: React.ReactNode; name: string; desc: string; available: boolean };
type Step = { num: string; title: string; desc: string };
type UseCase = { icon: React.ReactNode; title: string; desc: string };
type Faq = { q: string; a: string };

export interface HomeLandingProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  localizedPath: (p: string) => string;
  getToolHref: (id: string) => string;
  tools: Tool[];
  t: {
    heroTitle1: string; heroTitle2: string; heroDesc: string;
    ctaOpenEditor: string; ctaLearnMore: string; footer: string;
    badgeNoDataSent: string; badgeInstallFree: string;
    whyFreeTitle: string;
  };
  facts: { pricing: string; account: string; privacy: string; localBadge: string };
  home: {
    howItWorks: { title: string; steps: Step[] };
    useCases: { title: string; items: UseCase[] };
    homeFaq: { title: string; items: Faq[] };
  };
}

type Copy = {
  navTools: string; navPricing: string; navBlog: string; navOpen: string;
  eyebrow: string;
  s1Title: string; s1Desc: string; s1Link: string;
  s2Title: string; s2Desc: string; s2Link: string;
  s3Title: string; s3Desc: string; s3Link: string;
  toolsTitle: string; toolsSub: string;
  stat1: string; stat1Label: string; stat2: string; stat2Label: string;
  stat3: string; stat3Label: string; stat4: string; stat4Label: string;
  ctaTitle: string; ctaSub: string;
  altHero: string; altPlaylist: string; altPianoroll: string; altAutotune: string;
};

const COPY: Record<Lang, Copy> = {
  ja: {
    navTools: 'ツール', navPricing: '料金', navBlog: 'ブログ', navOpen: 'スタジオを開く',
    eyebrow: 'ブラウザで動くDAW',
    s1Title: '並べて、混ぜて、\n書き出す。',
    s1Desc: 'オーディオとMIDIを同じタイムラインに。カット・フェード・クオンタイズ・書き出しまで、インストールなしでそのまま。',
    s1Link: 'DAWを開く',
    s2Title: '音源もエフェクトも、\n最初から入っている。',
    s2Desc: 'ピアノロールとGM音源バンク、EQ・コンプ・リバーブ・リミッターのデバイスラック。AIマスターは1クリック。',
    s2Link: 'DAWエディタについて',
    s3Title: 'ノート単位で、\n音程を直す。',
    s3Desc: 'AIが歌声の一音一音を編集できるブロブとして描きます。ドラッグして音程を直し、そのまま試聴・WAV保存。',
    s3Link: 'Autotuneを試す',
    toolsTitle: 'AIスタジオに必要なもの、全部。',
    toolsSub: 'ボーカル除去からアンプシミュレーターまで。ほとんどはあなたのGPUで、ブラウザの中だけで動きます。',
    stat1: '端末内', stat1Label: '標準の音声処理',
    stat2: '0', stat2Label: 'インストール',
    stat3: '無料', stat3Label: 'コアツール',
    stat4: 'WebGPU', stat4Label: 'GPUアクセラレーション',
    ctaTitle: '今日から、ここで作る。',
    ctaSub: '登録不要。タブを開けばスタジオです。',
    altHero: 'LA Studio DAW：オーディオとMIDIトラックを並べたプレイリストと、ピアノ音源・EQ・コンプレッサー・リバーブを載せたデバイスラック',
    altPlaylist: 'ボーカルのオーディオトラックと3本のMIDIトラックを小節グリッドに並べたプレイリスト表示',
    altPianoroll: 'コードのノートが並ぶピアノロールと、その下のAcoustic Grand Piano・Channel EQ・Compressor・Reverbのデバイスラック',
    altAutotune: '検出した歌声のノートを編集可能なピッチ曲線としてピアノロール上に表示するAutotuneエディタ',
  },
  en: {
    navTools: 'Tools', navPricing: 'Pricing', navBlog: 'Blog', navOpen: 'Open Studio',
    eyebrow: 'A DAW that runs in your browser',
    s1Title: 'Arrange. Mix. Export.',
    s1Desc: 'Audio and MIDI on one timeline. Cut, fade, quantize, and bounce the mix — nothing to install, nothing to configure.',
    s1Link: 'Open the DAW',
    s2Title: 'Instruments and effects, built in.',
    s2Desc: 'A piano roll with a GM sound bank, plus a device rack of EQ, compressor, reverb, and limiter. AI mastering is one click.',
    s2Link: 'About the DAW editor',
    s3Title: 'Pitch editing, note by note.',
    s3Desc: 'Detected vocal notes appear as editable blobs. Detection can miss or split notes; check by ear. Drag to adjust pitch, audition, and download the WAV.',
    s3Link: 'Try Autotune',
    toolsTitle: 'Everything an AI studio should do.',
    toolsSub: 'From vocal removal to amp simulation. Most of it runs on your GPU, inside the browser tab.',
    stat1: 'On-device', stat1Label: 'standard audio processing',
    stat2: '0', stat2Label: 'installs',
    stat3: 'Free', stat3Label: 'core tools',
    stat4: 'WebGPU', stat4Label: 'accelerated AI',
    ctaTitle: 'Start making music.',
    ctaSub: 'No sign-up. Open a tab and you are in the studio.',
    altHero: 'LA Studio DAW: multitrack playlist with audio and MIDI tracks, and the device rack showing a piano instrument, EQ, compressor and reverb',
    altPlaylist: 'Playlist view with a vocal audio track and three MIDI tracks arranged on a bar grid',
    altPianoroll: 'Piano roll with chord notes and the device rack below showing Acoustic Grand Piano, Channel EQ, Compressor and Reverb',
    altAutotune: 'Autotune editor showing detected vocal notes as editable pitch curves on a piano-roll grid',
  },
  ko: {
    navTools: '도구', navPricing: '요금', navBlog: '블로그', navOpen: '스튜디오 열기',
    eyebrow: '브라우저에서 실행되는 DAW',
    s1Title: '배치하고, 섞고, 내보내기.',
    s1Desc: '오디오와 MIDI를 하나의 타임라인에. 자르기, 페이드, 퀀타이즈, 믹스 내보내기까지 설치 없이 바로.',
    s1Link: 'DAW 열기',
    s2Title: '악기와 이펙트가 기본 내장.',
    s2Desc: 'GM 사운드 뱅크가 있는 피아노 롤, EQ·컴프레서·리버브·리미터의 디바이스 랙. AI 마스터링은 클릭 한 번.',
    s2Link: 'DAW 에디터 알아보기',
    s3Title: '노트 단위로 음정을 고칩니다.',
    s3Desc: 'AI가 노래의 모든 음을 편집 가능한 블롭으로 그립니다. 드래그해서 음정을 맞추고 바로 듣고 WAV로 저장.',
    s3Link: 'Autotune 사용해 보기',
    toolsTitle: 'AI 스튜디오에 필요한 모든 것.',
    toolsSub: '보컬 제거부터 앰프 시뮬레이션까지. 대부분 브라우저 탭 안에서, 내 GPU로 실행됩니다.',
    stat1: '기기 내', stat1Label: '표준 오디오 처리',
    stat2: '0', stat2Label: '설치',
    stat3: '무료', stat3Label: '핵심 도구',
    stat4: 'WebGPU', stat4Label: 'GPU 가속 AI',
    ctaTitle: '지금 바로 음악을 만드세요.',
    ctaSub: '가입 불필요. 탭을 열면 스튜디오입니다.',
    altHero: 'LA Studio DAW: 오디오와 MIDI 트랙이 있는 멀티트랙 플레이리스트와 피아노 악기·EQ·컴프레서·리버브가 있는 디바이스 랙',
    altPlaylist: '보컬 오디오 트랙과 MIDI 트랙 3개를 마디 그리드에 배치한 플레이리스트 화면',
    altPianoroll: '코드 노트가 있는 피아노 롤과 아래의 Acoustic Grand Piano·Channel EQ·Compressor·Reverb 디바이스 랙',
    altAutotune: '감지된 보컬 노트를 편집 가능한 피치 곡선으로 피아노 롤에 표시하는 Autotune 편집기',
  },
  zh: {
    navTools: '工具', navPricing: '价格', navBlog: '博客', navOpen: '打开工作室',
    eyebrow: '在浏览器中运行的 DAW',
    s1Title: '编排、混音、导出。',
    s1Desc: '音频与 MIDI 同在一条时间线。剪切、淡入淡出、量化、导出混音——无需安装，无需配置。',
    s1Link: '打开 DAW',
    s2Title: '乐器与效果器，开箱即用。',
    s2Desc: '带 GM 音色库的钢琴卷帘，以及 EQ、压缩、混响、限制器的设备机架。AI 母带只需一键。',
    s2Link: '了解 DAW 编辑器',
    s3Title: '逐音符修正音高。',
    s3Desc: 'AI 将每个演唱音符绘制为可编辑的音块。拖动即可修正音高，即时试听并下载 WAV。',
    s3Link: '试用 Autotune',
    toolsTitle: 'AI 工作室应有的一切。',
    toolsSub: '从人声消除到音箱模拟。大多数功能在浏览器标签页内、由你的 GPU 运行。',
    stat1: '本地', stat1Label: '标准音频处理',
    stat2: '0', stat2Label: '安装',
    stat3: '免费', stat3Label: '核心工具',
    stat4: 'WebGPU', stat4Label: 'GPU 加速 AI',
    ctaTitle: '现在就开始创作。',
    ctaSub: '无需注册。打开一个标签页，你就在工作室里。',
    altHero: 'LA Studio DAW：包含音频与 MIDI 轨道的多轨播放列表，以及显示钢琴乐器、EQ、压缩器和混响的设备机架',
    altPlaylist: '播放列表视图：一条人声音频轨和三条 MIDI 轨排列在小节网格上',
    altPianoroll: '带和弦音符的钢琴卷帘，下方设备机架显示 Acoustic Grand Piano、Channel EQ、Compressor 和 Reverb',
    altAutotune: 'Autotune 编辑器：在钢琴卷帘网格上将检测到的人声音符显示为可编辑的音高曲线',
  },
  es: {
    navTools: 'Herramientas', navPricing: 'Precios', navBlog: 'Blog', navOpen: 'Abrir el estudio',
    eyebrow: 'Un DAW que funciona en tu navegador',
    s1Title: 'Organiza. Mezcla. Exporta.',
    s1Desc: 'Audio y MIDI en una sola línea de tiempo. Corta, aplica fundidos, cuantiza y exporta la mezcla — sin instalar ni configurar nada.',
    s1Link: 'Abrir el DAW',
    s2Title: 'Instrumentos y efectos, incluidos.',
    s2Desc: 'Un piano roll con banco de sonidos GM, más un rack de EQ, compresor, reverb y limitador. La masterización con IA es un clic.',
    s2Link: 'Sobre el editor DAW',
    s3Title: 'Edición de afinación, nota a nota.',
    s3Desc: 'La IA dibuja cada nota cantada como un bloque editable. Arrástralo para afinar, escucha al instante y descarga el WAV.',
    s3Link: 'Probar Autotune',
    toolsTitle: 'Todo lo que un estudio con IA debe hacer.',
    toolsSub: 'Desde quitar voces hasta simular amplificadores. Casi todo se ejecuta en tu GPU, dentro de la pestaña del navegador.',
    stat1: 'Local', stat1Label: 'procesamiento de audio estándar',
    stat2: '0', stat2Label: 'instalaciones',
    stat3: 'Gratis', stat3Label: 'herramientas básicas',
    stat4: 'WebGPU', stat4Label: 'IA acelerada',
    ctaTitle: 'Empieza a hacer música.',
    ctaSub: 'Sin registro. Abre una pestaña y ya estás en el estudio.',
    altHero: 'DAW de LA Studio: lista de reproducción multipista con pistas de audio y MIDI, y el rack de dispositivos con piano, EQ, compresor y reverb',
    altPlaylist: 'Vista de lista de reproducción con una pista vocal de audio y tres pistas MIDI en una cuadrícula de compases',
    altPianoroll: 'Piano roll con notas de acordes y, debajo, el rack con Acoustic Grand Piano, Channel EQ, Compressor y Reverb',
    altAutotune: 'Editor Autotune que muestra las notas vocales detectadas como curvas de afinación editables sobre un piano roll',
  },
  fr: {
    navTools: 'Outils', navPricing: 'Tarifs', navBlog: 'Blog', navOpen: 'Ouvrir le studio',
    eyebrow: 'Un DAW qui tourne dans votre navigateur',
    s1Title: 'Arrangez. Mixez. Exportez.',
    s1Desc: 'Audio et MIDI sur une même timeline. Coupez, fondez, quantifiez et exportez le mix — rien à installer, rien à configurer.',
    s1Link: 'Ouvrir le DAW',
    s2Title: 'Instruments et effets intégrés.',
    s2Desc: 'Un piano roll avec banque de sons GM, et un rack d’effets EQ, compresseur, réverbération et limiteur. Le mastering IA tient en un clic.',
    s2Link: 'À propos de l’éditeur DAW',
    s3Title: 'Corrigez la justesse, note par note.',
    s3Desc: 'L’IA dessine chaque note chantée sous forme de bloc modifiable. Glissez-le pour l’accorder, écoutez aussitôt et téléchargez le WAV.',
    s3Link: 'Essayer Autotune',
    toolsTitle: 'Tout ce qu’un studio IA doit savoir faire.',
    toolsSub: 'De la suppression de voix à la simulation d’ampli. La plupart s’exécute sur votre GPU, dans l’onglet du navigateur.',
    stat1: 'Local', stat1Label: 'traitement audio standard',
    stat2: '0', stat2Label: 'installation',
    stat3: 'Gratuit', stat3Label: 'outils essentiels',
    stat4: 'WebGPU', stat4Label: 'IA accélérée',
    ctaTitle: 'Commencez à faire de la musique.',
    ctaSub: 'Sans inscription. Ouvrez un onglet, vous êtes au studio.',
    altHero: 'DAW LA Studio : playlist multipiste avec pistes audio et MIDI, et rack d’effets affichant un piano, un EQ, un compresseur et une réverbération',
    altPlaylist: 'Vue playlist avec une piste audio vocale et trois pistes MIDI sur une grille de mesures',
    altPianoroll: 'Piano roll avec des notes d’accords et, dessous, le rack avec Acoustic Grand Piano, Channel EQ, Compressor et Reverb',
    altAutotune: 'Éditeur Autotune affichant les notes vocales détectées sous forme de courbes de hauteur modifiables sur un piano roll',
  },
  de: {
    navTools: 'Tools', navPricing: 'Preise', navBlog: 'Blog', navOpen: 'Studio öffnen',
    eyebrow: 'Eine DAW, die im Browser läuft',
    s1Title: 'Arrangieren. Mischen. Exportieren.',
    s1Desc: 'Audio und MIDI auf einer Timeline. Schneiden, faden, quantisieren und den Mix bouncen — nichts installieren, nichts einrichten.',
    s1Link: 'DAW öffnen',
    s2Title: 'Instrumente und Effekte sind schon drin.',
    s2Desc: 'Ein Piano-Roll mit GM-Soundbank und ein Geräte-Rack mit EQ, Kompressor, Hall und Limiter. KI-Mastering mit einem Klick.',
    s2Link: 'Über den DAW-Editor',
    s3Title: 'Tonhöhe bearbeiten, Note für Note.',
    s3Desc: 'Die KI zeichnet jede gesungene Note als bearbeitbaren Block. Ziehen Sie ihn in die richtige Tonhöhe, hören Sie sofort und laden Sie das WAV.',
    s3Link: 'Autotune ausprobieren',
    toolsTitle: 'Alles, was ein KI-Studio können sollte.',
    toolsSub: 'Von Gesangsentfernung bis Amp-Simulation. Das meiste läuft auf Ihrer GPU, direkt im Browser-Tab.',
    stat1: 'Lokal', stat1Label: 'Standard-Audioverarbeitung',
    stat2: '0', stat2Label: 'Installationen',
    stat3: 'Gratis', stat3Label: 'Kern-Tools',
    stat4: 'WebGPU', stat4Label: 'beschleunigte KI',
    ctaTitle: 'Fangen Sie an, Musik zu machen.',
    ctaSub: 'Ohne Anmeldung. Tab öffnen — Sie sind im Studio.',
    altHero: 'LA Studio DAW: Multitrack-Playlist mit Audio- und MIDI-Spuren und das Geräte-Rack mit Klavier, EQ, Kompressor und Hall',
    altPlaylist: 'Playlist-Ansicht mit einer Gesangs-Audiospur und drei MIDI-Spuren auf einem Taktraster',
    altPianoroll: 'Piano-Roll mit Akkordnoten und darunter das Rack mit Acoustic Grand Piano, Channel EQ, Compressor und Reverb',
    altAutotune: 'Autotune-Editor, der erkannte Gesangsnoten als bearbeitbare Tonhöhenkurven auf einem Piano-Roll-Raster zeigt',
  },
  pt: {
    navTools: 'Ferramentas', navPricing: 'Preços', navBlog: 'Blog', navOpen: 'Abrir o estúdio',
    eyebrow: 'Uma DAW que roda no seu navegador',
    s1Title: 'Organize. Mixe. Exporte.',
    s1Desc: 'Áudio e MIDI na mesma linha do tempo. Corte, aplique fades, quantize e exporte a mix — nada para instalar, nada para configurar.',
    s1Link: 'Abrir a DAW',
    s2Title: 'Instrumentos e efeitos já incluídos.',
    s2Desc: 'Um piano roll com banco de sons GM e um rack com EQ, compressor, reverb e limiter. Masterização com IA em um clique.',
    s2Link: 'Sobre o editor DAW',
    s3Title: 'Edição de afinação, nota por nota.',
    s3Desc: 'A IA desenha cada nota cantada como um bloco editável. Arraste para afinar, ouça na hora e baixe o WAV.',
    s3Link: 'Testar o Autotune',
    toolsTitle: 'Tudo o que um estúdio com IA deve fazer.',
    toolsSub: 'De remover vocais a simular amplificadores. Quase tudo roda na sua GPU, dentro da aba do navegador.',
    stat1: 'Local', stat1Label: 'processamento de áudio padrão',
    stat2: '0', stat2Label: 'instalações',
    stat3: 'Grátis', stat3Label: 'ferramentas principais',
    stat4: 'WebGPU', stat4Label: 'IA acelerada',
    ctaTitle: 'Comece a fazer música.',
    ctaSub: 'Sem cadastro. Abra uma aba e você já está no estúdio.',
    altHero: 'DAW da LA Studio: playlist multipista com faixas de áudio e MIDI, e o rack de dispositivos mostrando piano, EQ, compressor e reverb',
    altPlaylist: 'Vista de playlist com uma faixa de áudio vocal e três faixas MIDI numa grade de compassos',
    altPianoroll: 'Piano roll com notas de acordes e, abaixo, o rack com Acoustic Grand Piano, Channel EQ, Compressor e Reverb',
    altAutotune: 'Editor Autotune mostrando as notas vocais detectadas como curvas de afinação editáveis numa grade de piano roll',
  },
};

const Logo = () => (
  <svg width="140" height="28" viewBox="0 0 140 28" fill="none" aria-hidden="true">
    <text x="0" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#1a1610">L</text>
    <text x="16" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#d4a853">A</text>
    <rect x="38" y="7" width="2" height="14" rx="1" fill="#d4a853" />
    <text x="46" y="20" fontSize="14" fontFamily="'Montserrat', sans-serif" fontWeight="600" letterSpacing="3" fill="#8b7355">STUDIO</text>
  </svg>
);

const Arrow = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const HomeLanding: React.FC<HomeLandingProps> = ({
  lang, setLang, localizedPath, getToolHref, tools, t, facts, home,
}) => {
  const c = COPY[lang] || COPY.en;
  // Reveal animation is opt-in: without JS / IntersectionObserver every section
  // is simply visible (no hidden content for print, bots or old browsers).
  const [revealReady, setRevealReady] = useState(false);

  // Scroll reveal: elements with [data-reveal] fade/rise in once. CSS handles
  // prefers-reduced-motion (no transform, always visible).
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    setRevealReady(true);
    const els = Array.from(document.querySelectorAll<HTMLElement>('.hl [data-reveal]'));
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const openEditor = (trigger: string) => trackToolStart('editor', { trigger, lang });

  return (
    <div className={revealReady ? 'hl hl-js' : 'hl'} data-lang={lang}>
      <header className="hl-nav">
        <div className="hl-nav-inner">
          <Link className="hl-logo" href={localizedPath('/')} aria-label="LA Studio home"><Logo /></Link>
          <nav className="hl-nav-links">
            <a href="#tools">{c.navTools}</a>
            <Link href={localizedPath('/pricing')}>{c.navPricing}</Link>
            <Link href={localizedPath('/blog')}>{c.navBlog}</Link>
          </nav>
          <div className="hl-nav-actions">
            <select className="hl-lang" value={lang} onChange={(e) => setLang(e.target.value as Lang)} aria-label="Language">
              {LANGS.map((l) => (<option key={l} value={l}>{LANG_LABELS[l]}</option>))}
            </select>
            <HeaderAuthButton variant="landing" />
            <Link className="hl-pill hl-pill--gold hl-nav-open" href="/editor" onClick={() => openEditor('home_nav')}>
              {c.navOpen}
            </Link>
          </div>
        </div>
      </header>

      <main className="hl-main">
        {/* ===== Hero: the DAW itself ===== */}
        <section className="hl-hero">
          <p className="hl-eyebrow">{c.eyebrow}</p>
          <h1 className="hl-h1">
            {t.heroTitle1}<br />
            <span className="hl-gold">{t.heroTitle2}</span>
          </h1>
          <p className="hl-lede">{t.heroDesc}</p>
          <div className="hl-hero-cta">
            <Link className="hl-pill hl-pill--gold hl-pill--lg" href="/editor" onClick={() => openEditor('home_primary_cta')}>
              {t.ctaOpenEditor}
            </Link>
            <a className="hl-textlink" href="#tools">{t.ctaLearnMore} <Arrow /></a>
          </div>
          <div className="hl-hero-shot" data-reveal>
            <div className="hl-frame">
              <img
                src="/home/daw-hero.webp"
                width={1501}
                height={812}
                alt={c.altHero}
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
          <ul className="hl-hero-badges">
            <li>{t.badgeNoDataSent}</li>
            <li>{t.badgeInstallFree}</li>
            <li>WebGPU</li>
          </ul>
        </section>

        {/* ===== Feature strips ===== */}
        <section className="hl-strip" data-reveal>
          <div className="hl-strip-text">
            <h2>{c.s1Title}</h2>
            <p>{c.s1Desc}</p>
            <Link className="hl-textlink" href="/editor" onClick={() => openEditor('home_strip_playlist')}>{c.s1Link} <Arrow /></Link>
          </div>
          <div className="hl-frame hl-strip-shot">
            <img src="/home/daw-playlist.webp" width={1501} height={812} loading="lazy" decoding="async"
              alt={c.altPlaylist} />
          </div>
        </section>

        <section className="hl-strip hl-strip--dark" data-reveal>
          <div className="hl-strip-inner hl-strip-inner--reverse">
            <div className="hl-strip-text">
              <h2>{c.s2Title}</h2>
              <p>{c.s2Desc}</p>
              <Link className="hl-textlink" href={localizedPath('/daw-editor')}>{c.s2Link} <Arrow /></Link>
            </div>
            <div className="hl-frame hl-frame--dark hl-strip-shot">
              <img src="/home/daw-pianoroll.webp" width={1501} height={812} loading="lazy" decoding="async"
                alt={c.altPianoroll} />
            </div>
          </div>
        </section>

        <section className="hl-strip" data-reveal>
          <div className="hl-strip-text">
            <h2>{c.s3Title}</h2>
            <p>{c.s3Desc}</p>
            <Link className="hl-textlink" href={localizedPath('/autotune')} onClick={() => trackToolStart('autotune', { trigger: 'home_strip_autotune', lang })}>
              {c.s3Link} <Arrow />
            </Link>
          </div>
          <div className="hl-frame hl-strip-shot">
            <img src="/home/daw-autotune.webp" width={1501} height={812} loading="lazy" decoding="async"
              alt={c.altAutotune} />
          </div>
        </section>

        {/* ===== Stats ===== */}
        <section className="hl-stats" data-reveal>
          <div><strong>{c.stat1}</strong><span>{c.stat1Label}</span></div>
          <div><strong>{c.stat2}</strong><span>{c.stat2Label}</span></div>
          <div><strong>{c.stat3}</strong><span>{c.stat3Label}</span></div>
          <div><strong>{c.stat4}</strong><span>{c.stat4Label}</span></div>
        </section>

        {/* ===== Tool grid ===== */}
        <section className="hl-tools" id="tools" data-reveal>
          <h2 className="hl-h2">{c.toolsTitle}</h2>
          <p className="hl-sub">{c.toolsSub}</p>
          <div className="hl-tools-grid">
            {tools.map((tool) => (
              <Link
                key={tool.id}
                href={tool.available ? getToolHref(tool.id) : '#'}
                className={`hl-tool ${!tool.available ? 'is-disabled' : ''}`}
                aria-disabled={!tool.available}
                onClick={(e) => {
                  if (!tool.available) { e.preventDefault(); return; }
                  trackToolStart(tool.id, { trigger: 'home_tool_grid', lang });
                }}
              >
                <span className="hl-tool-icon">{tool.icon}</span>
                <strong>{tool.name}</strong>
                <span className="hl-tool-desc">{tool.desc}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* ===== How it works ===== */}
        <section className="hl-how" data-reveal>
          <h2 className="hl-h2">{home.howItWorks.title}</h2>
          <ol className="hl-how-steps">
            {home.howItWorks.steps.map((step, i) => (
              <li key={i}>
                <span className="hl-how-num">{step.num}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ===== Privacy / why free ===== */}
        <section className="hl-privacy" data-reveal>
          <div className="hl-privacy-card">
            <h2>{facts.localBadge}</h2>
            <p>{facts.privacy}</p>
          </div>
          <div className="hl-privacy-card">
            <h2>{t.whyFreeTitle}</h2>
            <p>{facts.pricing}</p>
            <p>{facts.account}</p>
          </div>
        </section>

        {/* ===== Use cases ===== */}
        <section className="hl-usecases" data-reveal>
          <h2 className="hl-h2">{home.useCases.title}</h2>
          <div className="hl-usecases-grid">
            {home.useCases.items.map((uc, i) => (
              <div key={i} className="hl-usecase">
                <span className="hl-usecase-icon">{uc.icon}</span>
                <h3>{uc.title}</h3>
                <p>{uc.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="hl-faq" data-reveal>
          <h2 className="hl-h2">{home.homeFaq.title}</h2>
          <div className="hl-faq-list">
            {home.homeFaq.items.map((faq, i) => (
              <details key={i} className="hl-faq-item">
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ===== Bottom CTA ===== */}
        <section className="hl-cta" data-reveal>
          <h2>{c.ctaTitle}</h2>
          <p>{c.ctaSub}</p>
          <Link className="hl-pill hl-pill--gold hl-pill--lg" href="/editor" onClick={() => openEditor('home_bottom_cta')}>
            {t.ctaOpenEditor}
          </Link>
        </section>
      </main>

      <footer className="hl-footer">
        <p>{t.footer}</p>
        <div className="hl-footer-links">
          <Link href={localizedPath('/about')}>About</Link>
          <Link href={localizedPath('/pricing')}>Pricing</Link>
          <Link href={localizedPath('/privacy')}>Privacy Policy</Link>
          <Link href={localizedPath('/terms')}>Terms of Service</Link>
          <Link href={localizedPath('/tokushoho')}>特定商取引法</Link>
          <Link href={localizedPath('/contact')}>Contact</Link>
          <Link href={localizedPath('/blog')}>Blog</Link>
        </div>
      </footer>
    </div>
  );
};

export default HomeLanding;
